import { InputCurrency } from "@/components/input-currency/InputCurrency";
import { InputField } from "@/components/input-field/InputField";
import { SelectComponent } from "@/components/select/Select";
import { TextArea } from "@/components/text-area/TextArea";
import { CategoriaServicio, UnidadCotizacionServicio } from "@/constants/enums";
import { AppColors } from "@/styles/colors";
import { standardNamePatternOne } from "@/utils/reg-expressions";
import { Card, Field, Flex, Grid, GridItem, Text } from "@chakra-ui/react";
import { TriangleAlert } from "lucide-react";
import { useEffect } from "react";
import {
  Control,
  FieldErrors,
  useController,
  useFieldArray,
  UseFormRegister,
  useWatch,
} from "react-hook-form";
import { ServiceMasterCreationForm } from "../create/schema/service-master-creation.schema";
import { getDosisRangesFeedback, isDosisUnidad } from "../utils/service-master-form";
import { ServiceMasterDosisRangeCard } from "./ServiceMasterDosisRangeCard";

interface Props {
  control: Control<ServiceMasterCreationForm>;
  errors: FieldErrors<ServiceMasterCreationForm>;
  register: UseFormRegister<ServiceMasterCreationForm>;
  loading?: boolean;
  showStatusField?: boolean;
}

const categoriaOptions = [
  { label: "Vuelo", value: CategoriaServicio.Vuelo },
  { label: "Muestreo", value: CategoriaServicio.Muestreo },
  { label: "Voleo", value: CategoriaServicio.Voleo },
];

const unidadCotizacionOptions = [
  { label: "Hectárea", value: UnidadCotizacionServicio.Hectarea },
  { label: "Cantidad/Muestra", value: UnidadCotizacionServicio.CantidadMuestra },
  { label: "Dosis", value: UnidadCotizacionServicio.Dosis },
];

export const ServiceMasterForm = ({
  control,
  errors,
  register,
  loading,
  showStatusField,
}: Props) => {
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "cotizacionesDosis",
  });

  const unidadCotizacion = useWatch({ control, name: "unidadCotizacion" });
  const isDosis = isDosisUnidad(unidadCotizacion);

  const watchedDosisRanges = useWatch({ control, name: "cotizacionesDosis" });
  const dosisRangesFeedback = isDosis
    ? getDosisRangesFeedback(watchedDosisRanges)
    : null;

  const { field: valorUnitarioField } = useController({
    control,
    name: "valorUnitario",
  });

  // Si se cambia "Cotiza por" a HA o Cantidad/Muestra, se descartan los rangos
  // cargados previamente (si se había elegido Dosis) para no dejar datos viejos
  // colgando ni mandarlos al guardar si el usuario vuelve a elegir Dosis.
  useEffect(() => {
    if (isDosis) return;

    replace([{ modalidad: [], dosisDesde: "", dosisHasta: "", valorUnitario: "" }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDosis]);

  // Si se elige "Dosis", se limpia el Valor Unitario general (el de HA /
  // Cantidad-Muestra) para no dejarlo colgado ni mandarlo al guardar si el
  // usuario vuelve a elegir Dosis, igual que con los rangos.
  useEffect(() => {
    if (!isDosis) return;

    valorUnitarioField.onChange("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDosis]);

  const handleAddDosisRange = () => {
    append({ modalidad: [], dosisDesde: "", dosisHasta: "", valorUnitario: "" });
  };

  return (
    <Flex flexDir="column" gap="24px" marginTop="30px">
      <Card.Root maxW="full" borderRadius="8px">
        <Card.Body
          paddingX="32px"
          paddingY="30px"
          borderRadius="8px"
          border={`1px solid ${AppColors.WHITE_BORDER_1}`}
        >
          <Grid templateColumns="repeat(4, 1fr)" gap={6}>
            <GridItem>
              <InputField
                inputName="titulo"
                labelText="Título"
                placeholder="Ingresar"
                control={control as any}
                disabled={loading}
                allowedPattern={standardNamePatternOne}
                required
                minHeight="40px"
                maxLength={255}
              />
            </GridItem>

            {!isDosis && (
              <GridItem>
                <Field.Root invalid={!!errors?.valorUnitario}>
                  <Field.Label>
                    <Text fontWeight={600}>
                      Valor Unitario
                      <Text as="span" color={AppColors.REQUIRED}>
                        {" *"}
                      </Text>
                    </Text>
                  </Field.Label>
                  <InputCurrency
                    control={control as any}
                    name="valorUnitario"
                    height="40px"
                    disabled={loading}
                  />
                  <Field.ErrorText>
                    {String(errors?.valorUnitario?.message ?? "")}
                  </Field.ErrorText>
                </Field.Root>
              </GridItem>
            )}

            <GridItem>
              <Field.Root invalid={!!errors?.categoria}>
                <SelectComponent
                  name="categoria"
                  items={categoriaOptions}
                  labelText="Pertenece a servicio"
                  placeholder="Seleccionar"
                  control={control as any}
                  clearable
                  disabled={loading}
                  minHeight="40px"
                  required
                />
                <Field.ErrorText>
                  {String(errors?.categoria?.message ?? "")}
                </Field.ErrorText>
              </Field.Root>
            </GridItem>

            <GridItem>
              <Field.Root invalid={!!errors?.unidadCotizacion}>
                <SelectComponent
                  name="unidadCotizacion"
                  items={unidadCotizacionOptions}
                  labelText="Cotiza por"
                  placeholder="Seleccionar"
                  control={control as any}
                  clearable
                  disabled={loading}
                  minHeight="40px"
                  required
                />
                <Field.ErrorText>
                  {String(errors?.unidadCotizacion?.message ?? "")}
                </Field.ErrorText>
              </Field.Root>
            </GridItem>

            {showStatusField && (
              <GridItem>
                <Field.Root invalid={!!(errors as any)?.activo}>
                  <SelectComponent
                    name="activo"
                    items={[
                      { label: "Activo", value: true },
                      { label: "Desactivado", value: false },
                    ]}
                    labelText="Estado"
                    placeholder="Seleccionar"
                    control={control as any}
                    clearable
                    disabled={loading}
                    minHeight="40px"
                    required
                  />
                  <Field.ErrorText>
                    {String((errors as any)?.activo?.message ?? "")}
                  </Field.ErrorText>
                </Field.Root>
              </GridItem>
            )}

            <GridItem colSpan={4}>
              <TextArea
                register={register as any}
                errors={errors}
                inputName="descripcion"
                labelText="Descripción"
                placeholder="Ingresar"
                disabled={loading}
                minHeight="80px"
              />
            </GridItem>
          </Grid>
        </Card.Body>
      </Card.Root>

      {isDosis && (
        <>
          {!!dosisRangesFeedback && (
            <Flex
              alignItems="center"
              gap="8px"
              paddingX="16px"
              paddingY="12px"
              borderRadius="6px"
              border={`1px solid ${
                dosisRangesFeedback.variant === "error"
                  ? AppColors.ERROR_TEXT
                  : AppColors.GRAY_BORDER_1
              }`}
              bg={dosisRangesFeedback.variant === "error" ? "red.50" : "gray.50"}
              data-testid="service-master-dosis-ranges-feedback"
            >
              <TriangleAlert
                color={
                  dosisRangesFeedback.variant === "error"
                    ? AppColors.ERROR_TEXT
                    : AppColors.GRAY_BORDER_1
                }
                size={18}
              />
              <Text
                color={
                  dosisRangesFeedback.variant === "error"
                    ? AppColors.ERROR_TEXT
                    : AppColors.GRAY_TEXT_ALT
                }
                fontWeight={600}
                fontSize="14px"
              >
                {dosisRangesFeedback.message}
              </Text>
            </Flex>
          )}

          {fields.map((field, index) => (
            <ServiceMasterDosisRangeCard
              key={field.id}
              index={index}
              control={control}
              errors={errors}
              dosisFieldsLength={fields.length}
              loading={loading}
              removeDosisRange={remove}
              handleAddDosisRange={handleAddDosisRange}
            />
          ))}
        </>
      )}
    </Flex>
  );
};
